import { Vector2 } from "../common/vector2.js";
import { Vfx2DRuntime } from "./vfx_2d_runtime.js";
import { MoveDirection } from "../common/types.js";
import { FireworksColor } from "./vfx/fireworks/fireworks_types.js";
import { VfxFireworksBatch } from "./vfx/fireworks/vfx_fireworks_batch.js";
import { VfxFireworks_BigSparks } from "./vfx/fireworks/vfx_fireworks_big_sparks.js";
import { VfxFireworks_Explosion } from "./vfx/fireworks/vfx_fireworks_explosion.js";
import { VfxFireworks_Sparks } from "./vfx/fireworks/vfx_fireworks_sparks.js";
import { VfxFireworks_Trail } from "./vfx/fireworks/vfx_fireworks_trail.js";
import { VfxConfettiBatch } from "./vfx/confetti/vfx_confetti_batch.js";
import { VfxConfettiSide } from "./vfx/confetti/vfx_confetti_side.js";
import { VfxConfettiBottom } from "./vfx/confetti/vfx_confetti_bottom.js";
import { VfxConfettiPoint } from "./vfx/confetti/vfx_confetti_point.js";
import { VfxUnlockChainOfferTask } from "./vfx/unlock_chain_offer_item/vfx_unlock_chain_offer_item_task.js";
import { VfxCoinFlyBatch } from "./vfx/coins/vfx_coin_fly_batch.js";
import { VfxCoinFly } from "./vfx/coins/vfx_coin_fly.js";
import { VfxCoinCounterGlowController } from "./vfx/coins/vfx_coin_counter_glow_controller.js";
import { VfxCoinCounterParticlesController } from "./vfx/coins/vfx_coin_counter_particles_controller.js";
import { VfxTaskRunner } from "./core/runtime/vfx_task_runner.js";
import { IController } from "./core/types/controller_types.js";
import { VfxBoosterHighlightGlowTask } from "./vfx/button_booster/vfx_button_booster_glow_task.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";

export class Vfx2DManager
{
  private static latest_coin_fly_finish_at = 0;

  // Один візуальний виліт монет часто розбитий на кілька addCoinsFly() з однаковим
  // start_time (по виклику на кожну іконку нагороди) — звук має бути один на групу.
  private static readonly COIN_FLY_SOUND_GROUP_MS = 250;
  private static last_coin_fly_sound_at = Number.NEGATIVE_INFINITY;

  // ========== Main Back layer ================================================================================================

  public static addButtonBoosterGlow( center: Vector2, size: Vector2 ): IController
  {
    const controller = new VfxBoosterHighlightGlowTask( center, size );
    VfxTaskRunner.addTask( controller );
    return controller;
  }

  public static addButtonBoosterGlowOnOverlay( center: Vector2, size: Vector2 ): IController
  {
    const controller = new VfxBoosterHighlightGlowTask( center, size, performance.now(), "overlay_back" );
    VfxTaskRunner.addTask( controller );
    return controller;
  }
  
  // ========== Main Front layer ================================================================================================

  public static addConfettiPoint( center: Vector2, start_time: number ): void
  {
    const batch = Vfx2DRuntime.onMainFront( VfxConfettiBatch );
    VfxConfettiPoint.burst( batch, center, start_time );
  }

  public static addSparkRing( center: Vector2, start_time: number, on_finish?: () => void ): IController
  {
    const task = new VfxUnlockChainOfferTask( center, start_time, on_finish );
    VfxTaskRunner.addTask( task );
    return task;
  }

  // ========== Overlay Back layer ================================================================================================

  public static addFireworksTrail( from: Vector2, to: Vector2, color: FireworksColor, amplitude_direction: MoveDirection, start_time: number ): void
  {
    const batch = Vfx2DRuntime.onOverlayBack( VfxFireworksBatch );
    VfxFireworks_Trail.spawn( batch, from, to, color, amplitude_direction, start_time );
    VfxFireworks_Sparks.spawn( batch, from, to, color, amplitude_direction, start_time );
    VfxFireworks_BigSparks.spawn( batch, from, to, color, amplitude_direction, start_time );
  }

  public static addFireworksExplosion( center: Vector2, color: FireworksColor, start_time: number ): void
  {
    const batch = Vfx2DRuntime.onOverlayBack( VfxFireworksBatch );
    VfxFireworks_Explosion.spawn( batch, center, color, start_time );
  }

  public static clearFireworks(): void
  {
    Vfx2DRuntime.onOverlayBack( VfxFireworksBatch ).killAll();
  }

  public static clearConfetti(): void
  {
    Vfx2DRuntime.onOverlayBack( VfxConfettiBatch ).killAll();
  }

  public static addConfettiSide( start_time: number ): void
  {
    const batch = Vfx2DRuntime.onOverlayBack( VfxConfettiBatch );
    VfxConfettiSide.burst( batch, start_time );
  }

  public static addConfettiBottom( start_time: number ): void
  {
    const batch = Vfx2DRuntime.onOverlayBack( VfxConfettiBatch );
    VfxConfettiBottom.burst( batch, start_time );
  }

  // ========== Overlay Front layer ================================================================================================

  // speed > 1 скорочує політ і розкид вильотів монет пропорційно.
  public static addCoinsFly( from: Vector2, to: Vector2, count: number, start_time: number, speed: number = 1 ): number
  {
    const batch = Vfx2DRuntime.onOverlayFront( VfxCoinFlyBatch );
    const finish_at = VfxCoinFly.burst( batch, from, to, count, start_time, speed );
    this.latest_coin_fly_finish_at = Math.max( this.latest_coin_fly_finish_at, finish_at );
    this.playCoinFlySound( start_time );
    return finish_at;
  }

  private static playCoinFlySound( start_time: number ): void
  {
    if ( Math.abs( start_time - this.last_coin_fly_sound_at ) < Vfx2DManager.COIN_FLY_SOUND_GROUP_MS )
      return;

    this.last_coin_fly_sound_at = start_time;

    // start_time буває в майбутньому (наприклад, COIN_FLY_DELAY_MS в GameResultOverlay) —
    // тоді звук чекає на реальний виліт монет.
    const delay = start_time - performance.now();
    if ( delay <= 0 )
    {
      void audioController.playSound( AudioClips.COINS_GROUP_FLY );
      return;
    }

    setTimeout( () => void audioController.playSound( AudioClips.COINS_GROUP_FLY ), delay );
  }

  public static getLatestCoinFlyFinishTime(): number
  {
    return this.latest_coin_fly_finish_at;
  }

  public static addCoinCounterImpact(
    target: Vector2,
    target_size: number,
    start_time: number,
    duration: number = 900,
    render_on_back: boolean = false,
    scale_in_duration: number = 0,
    persistent_glow: boolean = false,
    glow_rotation_speed?: number
  ): IController
  {
    VfxTaskRunner.addTask(
      new VfxCoinCounterParticlesController( target, target_size, duration, start_time, render_on_back, scale_in_duration )
    );
    const glow = new VfxCoinCounterGlowController(
      target,
      target_size,
      start_time,
      persistent_glow ? -1 : duration,
      render_on_back,
      scale_in_duration,
      glow_rotation_speed
    );
    VfxTaskRunner.addTask( glow );
    return glow;
  }

  // Same rotating rays glow as addCoinCounterImpact, without its sparkle-particle burst —
  // for callers that just want the glow (e.g. behind a static reward icon).
  public static addCoinCounterGlow(
    target: Vector2,
    target_size: number,
    start_time: number,
    duration: number = 900,
    render_on_back: boolean = false,
    scale_in_duration: number = 0,
    persistent_glow: boolean = false,
    glow_rotation_speed?: number
  ): IController
  {
    const glow = new VfxCoinCounterGlowController(
      target,
      target_size,
      start_time,
      persistent_glow ? -1 : duration,
      render_on_back,
      scale_in_duration,
      glow_rotation_speed
    );
    VfxTaskRunner.addTask( glow );
    return glow;
  }
}
